import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Clock, AlertTriangle, CheckCircle2, CreditCard, Sparkles } from "lucide-react";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

const STATUS_META = {
  active: { label: "Aktif", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  trial: { label: "Masa Trial", cls: "bg-sky-100 text-sky-700", icon: Clock },
  expired: { label: "Berakhir", cls: "bg-rose-100 text-rose-700", icon: AlertTriangle },
  pending_payment: { label: "Menunggu Pembayaran", cls: "bg-amber-100 text-amber-700", icon: Clock },
  suspended: { label: "Ditangguhkan", cls: "bg-rose-100 text-rose-700", icon: AlertTriangle },
};

export default function OwnerSubscriptionPage() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState("");
  const [methodModal, setMethodModal] = useState(null); // {plan, methods, amount}
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get("/subscription/me").then((r) => setSub(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Returning from Duitku: poll payment status once.
  useEffect(() => {
    const order = params.get("order");
    if (!order) return;
    apiClient.get(`/subscription/payment/${order}/check`).then((r) => {
      if (r.data.payment_status === "success") toast.success("Pembayaran berhasil! Subscription aktif.");
      else if (r.data.payment_status === "pending") toast.info("Pembayaran masih diproses. Mohon tunggu.");
      else toast.error("Pembayaran belum berhasil.");
      params.delete("order"); setParams(params, { replace: true });
      load();
    }).catch(() => {});
  }, []); // eslint-disable-line

  const startPay = async (plan) => {
    setPaying(plan.id);
    try {
      const { data } = await apiClient.get(`/subscription/pay/methods/${plan.id}`);
      if (data.free) { await payWith(plan.id, null); return; }
      setMethodModal({ plan, methods: data.methods, amount: data.amount });
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setPaying(""); }
  };

  const payWith = async (planId, method) => {
    setPaying(planId);
    try {
      const { data } = await apiClient.post("/subscription/pay", { plan_id: planId, payment_method: method });
      if (data.free) { toast.success("Subscription aktif."); setMethodModal(null); load(); }
      else if (data.payment_url) window.location.href = data.payment_url;
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setPaying(""); }
  };

  if (loading) return <OwnerLayout testid="owner-subscription-page"><div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div></OwnerLayout>;

  const st = sub?.status || "expired";
  const meta = STATUS_META[st] || STATUS_META.expired;
  const StatusIcon = meta.icon;
  const plans = sub?.plans || [];

  return (
    <OwnerLayout testid="owner-subscription-page">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={22} className="text-primary" />
        <h1 className="font-heading text-3xl font-bold tracking-tight">Subscription</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Kelola masa aktif akun Owner Anda di AVASA.</p>

      {/* Status card */}
      <div className="bg-card border border-border rounded-2xl p-6 mb-6" data-testid="subscription-status-card">
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${meta.cls}`} data-testid="subscription-status-badge">
            <StatusIcon size={15} /> {meta.label}
          </span>
          {sub?.days_left != null && (st === "trial" || st === "active") && (
            <span className="text-sm text-muted-foreground" data-testid="subscription-days-left">Tersisa <b className="text-foreground">{sub.days_left} hari</b></span>
          )}
        </div>
        {sub?.gated ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Akses Owner Anda telah berakhir. Silakan lakukan pembayaran untuk mengaktifkan kembali akses ke dashboard, properti, dan web-site Anda.
          </p>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">
            {st === "trial" ? "Anda sedang dalam masa trial. Perpanjang dengan berlangganan agar akses tidak terputus." : "Subscription Anda aktif. Terima kasih!"}
            {sub?.plan?.name && <> Paket saat ini: <b className="text-foreground">{sub.plan.name}</b>.</>}
          </p>
        )}
        {sub?.warn != null && !sub?.gated && (
          <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="subscription-warning">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>Akses Anda akan berakhir dalam {sub.days_left} hari. Perpanjang sekarang agar properti Anda tetap aktif.</span>
          </div>
        )}
      </div>

      {/* Plans */}
      <h2 className="font-heading text-lg font-semibold mb-3">Pilih Paket</h2>
      {plans.length === 0 ? (
        <p className="text-sm text-muted-foreground">Belum ada paket tersedia. Hubungi Admin AVASA.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="subscription-plans">
          {plans.map((p) => (
            <div key={p.id} className="bg-card border border-border rounded-2xl p-6 flex flex-col hover:border-primary/40 transition-colors" data-testid={`plan-${p.id}`}>
              <div className="flex items-center gap-2 mb-1"><Sparkles size={16} className="text-accent" /><h3 className="font-heading text-lg font-semibold">{p.name}</h3></div>
              {p.description && <p className="text-xs text-muted-foreground mb-3">{p.description}</p>}
              <p className="text-2xl font-bold tracking-tight">{p.price > 0 ? rupiah(p.price) : "Gratis"}</p>
              <p className="text-xs text-muted-foreground mb-4">{p.duration_days} hari</p>
              <button onClick={() => startPay(p)} disabled={!!paying} data-testid={`plan-pay-${p.id}`}
                className="mt-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60">
                {paying === p.id ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Bayar Sekarang
              </button>
            </div>
          ))}
        </div>
      )}

      {methodModal && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-black/60 p-4" data-testid="method-modal" onClick={() => setMethodModal(null)}>
          <div className="w-full max-w-md rounded-2xl bg-card border border-border p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-semibold mb-1">Pilih Metode Pembayaran</h3>
            <p className="text-sm text-muted-foreground mb-4">{methodModal.plan.name} · Rp {Number(methodModal.amount).toLocaleString("id-ID")}</p>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {methodModal.methods.map((m) => (
                <button key={m.code} onClick={() => payWith(methodModal.plan.id, m.code)} disabled={!!paying}
                  data-testid={`method-${m.code}`}
                  className="w-full flex items-center gap-3 rounded-xl border border-border p-3 hover:border-primary/50 hover:bg-secondary/40 transition-colors text-left disabled:opacity-60">
                  {m.image ? <img src={m.image} alt={m.name} className="h-6 w-auto object-contain" /> : <CreditCard size={18} className="text-muted-foreground" />}
                  <span className="flex-1 text-sm font-medium">{m.name}</span>
                  {paying === methodModal.plan.id && <Loader2 size={15} className="animate-spin" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </OwnerLayout>
  );
}
